// /src/components/dev/DevLoginModal.tsx
import { useEffect, useRef } from "react";

type DevLoginModalProps = {
  open: boolean;
  busy: boolean;
  password: string;
  error: string;
  onPasswordChange: (next: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
};

export function DevLoginModal(props: DevLoginModalProps) {
  const { open, busy, password, error, onPasswordChange, onCancel, onSubmit } = props;

  const cardRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Focus the card so keydown works reliably, then focus input on open.
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      onMouseDown={(e) => {
        // Close if clicking the overlay (not the modal card)
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
        if (e.key === "Enter" && !busy) onSubmit();
      }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        ref={cardRef}
        tabIndex={-1}
        className="relative z-10 w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-xl p-4 outline-none"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Developer tools sign-in</div>
          <button type="button" className="text-sm underline" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>

        <div className="text-sm text-gray-600 mb-3">
          Enter the current developer password to enable dev mode and admin tools.
        </div>

        <input
          ref={inputRef}
          type="password"
          className="w-full border border-gray-300 rounded-xl p-2 text-sm"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Developer password"
          disabled={busy}
        />

        {error ? (
          <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-2">
            {error}
          </div>
        ) : null}

        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-xl px-3 py-2 text-sm border border-gray-300"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`rounded-xl px-3 py-2 text-sm font-semibold ${
              busy ? "bg-gray-200 text-gray-500" : "bg-black text-white"
            }`}
            onClick={onSubmit}
            disabled={busy}
          >
            {busy ? "Signing in…" : "Enable developer tools"}
          </button>
        </div>
      </div>
    </div>
  );
}